/**
 * Category Products Modal
 *
 * Modal to preview all products in a category for a selected store.
 * Features: stock-sorted display, server-side search, infinite scroll.
 *
 * @module HomeLayoutManager/components/CategoryProductsModal
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Tooltip,
  SelectChangeEvent,
  ListSubheader,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  NewReleases as NewIcon,
  TrendingUp as PopularIcon,
  Warning as RestrictedIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import {
  fetchStores,
  fetchCategoryProductCountByName as fetchCategoryProductCount,
  fetchCategoryProductsPaginated,
} from '../api';
import type { Store } from '../types';
import { useStoreVisibility } from '../../../hooks/useStoreVisibility';

/**
 * Product data structure
 */
interface Product {
  id: string;
  name: string;
  price: number;
  priceTier1?: number;
  stock: number;
  sku?: string;
  barcode?: string;
  status?: string;
  isNew?: boolean;
  isPopular?: boolean;
  isRestricted?: boolean;
  isSellable?: boolean;
  isSellableInApp?: boolean;
  attachments?: Array<{ id?: string; url?: string; thumbnailUrl?: string; downloadUrl?: string; type?: string }>;
  brand?: string | { name: string };
  tags?: Array<string | { name: string }>;
}

/** Page size for infinite scroll */
const PAGE_SIZE = 50;

/** Debounce delay for search input (ms) */
const SEARCH_DEBOUNCE_MS = 400;

interface CategoryProductsModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Category ID */
  categoryId: string;
  /** Category name */
  categoryName: string;
  /** Optional initial store ID */
  initialStoreId?: string;
}

/**
 * Get brand name from product
 */
function getBrandName(product: Product): string | undefined {
  if (!product.brand) return undefined;
  if (typeof product.brand === 'string') return product.brand;
  return product.brand.name;
}

/**
 * Format price for display
 */
function formatPrice(price: number | undefined): string {
  if (price === undefined || price === null) return '-';
  return `R${(price / 100).toFixed(2)}`;
}

/**
 * Category Products Modal Component
 */
export default function CategoryProductsModal({
  open,
  onClose,
  categoryId,
  categoryName,
  initialStoreId,
}: CategoryProductsModalProps): React.ReactElement {
  const { isStoreVisible } = useStoreVisibility();
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>(initialStoreId || '');
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingStores, setIsLoadingStores] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [storeProductCounts, setStoreProductCounts] = useState<Record<string, number>>({});
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch stores and product counts on mount
  // IMPORTANT: Products API expects externalId, not internal UUID
  useEffect(() => {
    if (open && categoryName) {
      setIsLoadingStores(true);
      setIsLoadingCounts(true);
      // Reset to initialStoreId when modal opens (or empty to auto-select)
      setSelectedStoreId(initialStoreId || '');
      setProducts([]);
      setSearchQuery('');
      setDebouncedSearch('');
      setPage(1);
      setTotal(0);
      setHasMore(false);

      fetchStores().then(async (storeData) => {
        const visible = storeData.filter(isStoreVisible);
        setStores(visible);
        setIsLoadingStores(false);

        // Fetch product counts in small batches to avoid 429 rate limiting
        // Use externalId for API calls, but key by internal id for state
        const counts: Record<string, number> = {};
        const batchSize = 3;
        for (let i = 0; i < visible.length; i += batchSize) {
          const batch = visible.slice(i, i + batchSize);
          await Promise.all(
            batch.map(async (store) => {
              const count = await fetchCategoryProductCount(categoryName, store.externalId || store.id);
              counts[store.id] = count;
            })
          );
          setStoreProductCounts({ ...counts });
          if (i + batchSize < visible.length) {
            await new Promise((r) => setTimeout(r, 200));
          }
        }
        setIsLoadingCounts(false);

        // If initialStoreId was provided, always use it (even if 0 products)
        // Otherwise auto-select first store with products
        if (initialStoreId) {
          setSelectedStoreId(initialStoreId);
        } else {
          const storeWithProducts = visible.find((s) => counts[s.id] > 0);
          setSelectedStoreId(storeWithProducts?.id || visible[0]?.id || '');
        }
      });
    }
  }, [open, categoryName, initialStoreId]);

  // Sorted stores - stores with products first
  const sortedStores = useMemo(() => {
    const withProducts = stores.filter((s) => storeProductCounts[s.id] > 0);
    const withoutProducts = stores.filter((s) => !storeProductCounts[s.id] || storeProductCounts[s.id] === 0);
    return { withProducts, withoutProducts };
  }, [stores, storeProductCounts]);

  // Fetch products when store or debounced search changes (reset to page 1)
  // IMPORTANT: Products API expects externalId, not internal UUID
  useEffect(() => {
    if (!open || !selectedStoreId || !categoryName) return;

    const selectedStore = stores.find((s) => s.id === selectedStoreId);
    const storeExternalId = selectedStore?.externalId || selectedStoreId;

    setPage(1);
    setIsLoadingProducts(true);

    fetchCategoryProductsPaginated(categoryName, storeExternalId, {
      page: 1,
      limit: PAGE_SIZE,
      search: debouncedSearch || undefined,
    }).then((result) => {
      setProducts(result.data as Product[]);
      setTotal(result.total);
      setHasMore(result.data.length < result.total);
      setIsLoadingProducts(false);
    });
  }, [open, selectedStoreId, categoryName, stores, debouncedSearch]);

  // Load more products (append to existing list)
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || !selectedStoreId) return;

    const selectedStore = stores.find((s) => s.id === selectedStoreId);
    const storeExternalId = selectedStore?.externalId || selectedStoreId;
    const nextPage = page + 1;

    setIsLoadingMore(true);

    fetchCategoryProductsPaginated(categoryName, storeExternalId, {
      page: nextPage,
      limit: PAGE_SIZE,
      search: debouncedSearch || undefined,
    }).then((result) => {
      setProducts((prev) => {
        const updated = [...prev, ...(result.data as Product[])];
        setHasMore(updated.length < result.total);
        return updated;
      });
      setPage(nextPage);
      setIsLoadingMore(false);
    });
  }, [isLoadingMore, hasMore, page, stores, selectedStoreId, categoryName, debouncedSearch]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoadingProducts) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, isLoadingProducts, loadMore]);

  // Sort products: stock > 0 first, then by name
  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => {
      if (a.stock > 0 && b.stock <= 0) return -1;
      if (a.stock <= 0 && b.stock > 0) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [products]);

  // Handle store change
  const handleStoreChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedStoreId(event.target.value);
  }, []);

  // Get selected store name
  const selectedStore = stores.find((s) => s.id === selectedStoreId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: '#FF002C', width: 36, height: 36 }}>
              <CategoryIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" component="span">
                {categoryName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Products in this category
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Sticky Controls */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
          }}
        >
          {/* Store Selector */}
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Store</InputLabel>
            <Select
              value={isLoadingStores ? '' : selectedStoreId}
              onChange={handleStoreChange}
              label="Store"
              disabled={isLoadingStores || isLoadingCounts}
            >
              {/* Stores with products */}
              {sortedStores.withProducts.length > 0 && (
                <ListSubheader sx={{ bgcolor: 'success.light', color: 'success.contrastText', lineHeight: '32px' }}>
                  Stores with products ({sortedStores.withProducts.length})
                </ListSubheader>
              )}
              {sortedStores.withProducts.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Typography variant="body2">{store.name}</Typography>
                    <Chip
                      label={`${storeProductCounts[store.id]} products`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </MenuItem>
              ))}

              {/* Divider between sections */}
              {sortedStores.withProducts.length > 0 && sortedStores.withoutProducts.length > 0 && (
                <Divider />
              )}

              {/* Stores without products */}
              {sortedStores.withoutProducts.length > 0 && (
                <ListSubheader sx={{ bgcolor: 'grey.200', color: 'text.secondary', lineHeight: '32px' }}>
                  No products in this category
                </ListSubheader>
              )}
              {sortedStores.withoutProducts.map((store) => (
                <MenuItem
                  key={store.id}
                  value={store.id}
                  sx={{
                    opacity: 0.5,
                    '&:hover': { opacity: 0.7 },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{store.name}</Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                      No products
                    </Typography>
                  </Box>
                </MenuItem>
              ))}

              {/* Loading indicator */}
              {isLoadingCounts && stores.length > 0 && (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Loading product counts...</Typography>
                  </Box>
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Search (server-side) */}
          <TextField
            size="small"
            placeholder="Search all products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 300 }}
          />

          {/* Product count */}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            {debouncedSearch && (
              <Typography variant="caption" color="text.secondary">
                Searching: &quot;{debouncedSearch}&quot;
              </Typography>
            )}
            <Chip
              icon={<InventoryIcon />}
              label={
                products.length < total
                  ? `${products.length} of ${total} product${total !== 1 ? 's' : ''}`
                  : `${total} product${total !== 1 ? 's' : ''}`
              }
              color={total > 0 ? 'primary' : 'default'}
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Content area with padding */}
        <Box sx={{ p: 2 }}>
          {/* Loading state */}
          {(isLoadingStores || isLoadingProducts || isLoadingCounts) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Empty state */}
          {!isLoadingStores && !isLoadingProducts && !isLoadingCounts && sortedProducts.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {debouncedSearch
                  ? `No products match "${debouncedSearch}"`
                  : `No products found in "${categoryName}" at ${selectedStore?.name || 'this store'}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {debouncedSearch
                  ? 'Try a different search term'
                  : 'Products may not be assigned to this category or may be out of stock'}
              </Typography>
            </Box>
          )}

          {/* Products table */}
          {!isLoadingStores && !isLoadingProducts && !isLoadingCounts && sortedProducts.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={60}>Image</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Stock</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedProducts.map((product) => {
                    const imageUrl =
                      product.attachments?.[0]?.thumbnailUrl ||
                      product.attachments?.[0]?.downloadUrl ||
                      product.attachments?.[0]?.url;

                    return (
                      <TableRow
                        key={product.id}
                        sx={{
                          opacity: product.stock > 0 ? 1 : 0.5,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          <Avatar
                            src={imageUrl}
                            variant="rounded"
                            sx={{ width: 40, height: 40, bgcolor: 'grey.200' }}
                          >
                            <CategoryIcon fontSize="small" />
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 250 }}>
                            {product.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {product.sku || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {getBrandName(product) || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {formatPrice(product.priceTier1 || product.price)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={product.stock}
                            size="small"
                            color={product.stock > 0 ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ minWidth: 50, height: 22 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {product.isNew && (
                              <Tooltip title="New">
                                <NewIcon sx={{ fontSize: 16, color: 'info.main' }} />
                              </Tooltip>
                            )}
                            {product.isPopular && (
                              <Tooltip title="Popular">
                                <PopularIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                              </Tooltip>
                            )}
                            {product.isRestricted && (
                              <Tooltip title="Restricted (age verification)">
                                <RestrictedIcon sx={{ fontSize: 16, color: 'error.main' }} />
                              </Tooltip>
                            )}
                            {!product.isSellable && (
                              <Tooltip title="Not sellable">
                                <Chip label="Disabled" size="small" sx={{ height: 18, fontSize: 10 }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} style={{ height: 1 }} />

          {/* Loading more indicator */}
          {isLoadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, gap: 1, alignItems: 'center' }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading more products...
              </Typography>
            </Box>
          )}

          {/* End of list indicator */}
          {!isLoadingMore && !hasMore && products.length > 0 && products.length >= PAGE_SIZE && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
              All {total} products loaded
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
